/**
 * Automatic fixing driven by the server's attempt count (PROTOTYPER_STABILITY_TECH_PLAN.md
 * P6.4b). Replaces the one-fix-per-turn `localStorage` record (`resumeFixAttempts.ts`) whenever
 * the versions API answers; that record stays the fallback while it does not.
 *
 *   build fails ─▶ fail {kind: code} ─▶ fixAllowed? ─▶ fix stream (`turnKind: fix`; the AI API
 *                                         │               claims the attempt, doesn't bill it)
 *                                         └─ no ─▶ card: Try another fix / Undo / Tell the AI
 *
 * Kept dependency-free so it stays unit-testable in the node test env.
 */

import { TURN_BUDGET_SIGNATURE } from './turnBudget';

/** What the card needs about the version that stopped automatic fixing. */
export interface VersionAttention {
  llmInteractionId: string;
  versionId: string;
  fixAttempts: number;
  fixAttemptsGranted: number;
  /** Set when the founder has no credits for another attempt. */
  notice?: string | null;
  /** Set when fixing stopped because the turn ran out of time, not because of the attempts. */
  reason?: 'timeout' | null;
}

export type AfterFailureAction = 'fix' | 'needs_input' | 'no_credits' | 'none';

export const NO_CREDITS_FOR_FIX_NOTICE =
  'Add credits to keep fixing this build. Your last working version is still available.';

/**
 * - `fix`: start an automatic fix stream.
 * - `needs_input`: automatic fixing stopped (limit reached, or the same error again).
 * - `no_credits`: a fix is allowed but the founder has no credits; show the card with the
 *   billing notice instead of spending.
 * - `none`: nothing to do — the versions API is unavailable (callers keep the pre-versions
 *   behaviour) or the failure was not the code's fault (an environment failure keeps a draft).
 */
export function decideAfterVersionFailure(args: {
  result: { status: string; fixAllowed: boolean } | null;
  hasCredits: boolean;
}): AfterFailureAction {
  const { result, hasCredits } = args;
  if (!result) return 'none';
  if (result.fixAllowed) return hasCredits ? 'fix' : 'no_credits';
  return result.status === 'failed' || result.status === 'failed_needs_input'
    ? 'needs_input'
    : 'none';
}

/** Timeline / card label while a fix stream runs. `fixAttempts` counts the running attempt. */
export function fixProgressLabel(fixAttempts: number, fixAttemptsGranted: number): string {
  if (fixAttempts <= 0 || fixAttemptsGranted <= 0) return 'Fixing a build error…';
  return `Fixing a build error (attempt ${Math.min(fixAttempts, fixAttemptsGranted)} of ${fixAttemptsGranted})…`;
}

interface VersionLike {
  id: string;
  llmInteractionId: string | null;
  status: string;
  fixAttempts: number;
  fixAttemptsGranted: number;
  createdAt: string;
  lastErrorSignature?: string | null;
}

/**
 * After a reload: the newest version that is waiting for the founder, from the conversation's
 * prototypes (`GET …/prototype/prototypes`). Null when none is.
 */
export function attentionFromPrototypes(
  prototypes: ReadonlyArray<{ latestVersion: VersionLike | null }>
): VersionAttention | null {
  let newest: VersionLike | null = null;
  for (const prototype of prototypes) {
    const version = prototype.latestVersion;
    if (!version || version.status !== 'failed_needs_input' || !version.llmInteractionId) continue;
    if (!newest || version.createdAt > newest.createdAt) newest = version;
  }
  return newest
    ? {
        llmInteractionId: newest.llmInteractionId!,
        versionId: newest.id,
        fixAttempts: newest.fixAttempts,
        fixAttemptsGranted: newest.fixAttemptsGranted,
        ...(newest.lastErrorSignature === TURN_BUDGET_SIGNATURE
          ? { reason: 'timeout' as const }
          : {}),
      }
    : null;
}

/**
 * The prototypes whose failed build may ask this conversation for a decision. A startup's shared
 * prototype (User Story 569) lists builds made in every scope, but fixing or building one again
 * stays with the scope that made it: another scope's failed build is left out here, so its card
 * shows only there. `ownTurnIds` are this conversation's turns.
 */
export function prototypesForAttention<
  T extends {
    startupPrototype?: boolean;
    latestVersion: { llmInteractionId: string | null } | null;
  },
>(prototypes: readonly T[], ownTurnIds: ReadonlySet<string>): T[] {
  return prototypes.filter((prototype) => {
    if (prototype.startupPrototype !== true) return true;
    const turnId = prototype.latestVersion?.llmInteractionId;
    return turnId != null && ownTurnIds.has(turnId);
  });
}
